import { h } from 'preact'
import { useSignal } from '@preact/signals'
import { useEffect } from 'preact/hooks'
import { TaskCard } from './TaskCard.jsx'
import { chatApi } from '../chat-api.js'

const TASK_TOKEN_REGEX = /#([A-Z]+-\d+)/g

/**
 * TaskReferenceStrip — horizontal scrollable row of TaskCard previews shown
 * above the composer when the user types a #TOKEN reference in the message.
 *
 * Extracts unique task tokens from inputText (e.g. #TSH-123, #PROJ-42),
 * fetches each task once, and renders a strip of <TaskCard> previews.
 *
 * Props: { inputText, store }
 */
export function TaskReferenceStrip({ inputText, store }) {
  const taskCache = useSignal({}) // { token: taskDataOrFalse }

  // Extract unique tokens from input
  const tokens = uniqueTokens(inputText || '')

  // Fetch missing tokens
  useEffect(() => {
    tokens.forEach(token => {
      if (taskCache.value[token] !== undefined) return // already fetched or fetching

      // Mark as fetching
      taskCache.value = { ...taskCache.value, [token]: null }

      const id = tokenToId(token)
      chatApi.tasks.show(id).then(async res => {
        if (res.ok) {
          const data = await res.json
          taskCache.value = {
            ...taskCache.value,
            [token]: data.task ?? data,
          }
        } else {
          taskCache.value = { ...taskCache.value, [token]: false }
        }
      }).catch(() => {
        taskCache.value = { ...taskCache.value, [token]: false }
      })
    })
  }, [tokens.join(',')]) // eslint-disable-line

  // Determine which tokens have loaded data
  const loadedTokens = tokens.filter(t => taskCache.value[t] !== undefined)
  if (!loadedTokens.length) return null

  return (
    <div class="chat-task-reference-strip">
      {loadedTokens.map(token => (
        <TaskCard
          key={token}
          taskId={tokenToId(token)}
          store={store}
        />
      ))}
    </div>
  )
}

function uniqueTokens(text) {
  const matches = []
  const seen    = new Set()
  let   m
  TASK_TOKEN_REGEX.lastIndex = 0
  while ((m = TASK_TOKEN_REGEX.exec(text)) !== null) {
    const token = m[1]
    if (!seen.has(token)) {
      seen.add(token)
      matches.push(token)
    }
  }
  return matches
}

/** Derive a task identifier from a token like TSH-123. */
function tokenToId(token) {
  return token // chatApi.tasks.show accepts either reference_code or numeric id
}
